import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import Navbar from './BasicComponents/Navbar';
import AppRoutes from './Routes/routes';
import { UserProvider } from './BasicComponents/UserContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SubscriptionProvider } from './Context/SubscriptionContext';
import { SidebarProvider } from './Context/SidebarContext';

import { setupAxiosInterceptors } from './utils/axiosInterceptor';
import ScrollToTop from './utils/ScrollToTop';
import RouteSeo from './seo/RouteSeo';
import './Styles/tenant-theme.css';

import ErrorBoundary from './error-framework/ErrorBoundary';
import TsgAiBotGate from './components/TsgAiBot/TsgAiBotGate';
import { ErrorProvider } from './error-framework/ErrorContext';
import ErrorExperience from './error-framework/ErrorExperience';
import { OfflineBanner } from './error-framework/ErrorStates';

// Shown while a route's code chunk downloads (routes are lazy-loaded in
// Routes/routes.jsx). Holds the page height so the footer doesn't jump up,
// and waits a beat before showing anything so fast loads never flash it.
const RouteFallback = () => (
  <div
    role="status"
    aria-live="polite"
    style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
  >
    <span className="route-fallback-spinner" aria-hidden="true" />
    <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Loading…</span>
  </div>
);

// Inner component to access navigate hook
const AppContent = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Setup axios interceptors with navigate function
    setupAxiosInterceptors(navigate);
  }, [navigate]);

  // Broadcast-only: dispatching an event cannot change any existing
  // behaviour, because nothing is required to listen for it. Screens that
  // want automatic offline recovery can opt in with a single useEffect.
  const handleReconnect = React.useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent('tsg:reconnected'));
    } catch (e) {
      /* recovery signalling must never throw */
    }
  }, []);

  return (
    // ErrorProvider needs to be inside BrowserRouter (it reads the current
    // route via useLocation for context-aware messages) — see
    // error-framework/ErrorContext.jsx.
    <ErrorProvider>
      {/*
        Renders nothing while online, so there is no layout or paint cost
        on the happy path. On reconnect it fires a single app-wide event
        that data-loading screens can listen for to refetch themselves,
        which is what makes recovery automatic rather than a manual refresh.
      */}
      <OfflineBanner onReconnect={handleReconnect} />
      {/* Route-level <head> metadata (title, canonical, robots, OG, JSON-LD) —
          data-driven from src/seo/routes.config.js. Renders nothing. */}
      <RouteSeo />
      <ScrollToTop />
      <Navbar />
      <Suspense fallback={<RouteFallback />}>
        <AppRoutes />
      </Suspense>
      {/* Tee Gee — public visitor AI guide; hides itself on app/admin routes */}
      <TsgAiBotGate />
      <ErrorExperience />
    </ErrorProvider>
  );
};

function App() {
  return (
    // Top-level safety net: previously, an uncaught render error anywhere
    // in the tree produced a blank white screen. ErrorBoundary is purely
    // additive — it only activates on an actual crash.
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <UserProvider>
            <SubscriptionProvider>
              <SidebarProvider>
                <AppContent />
              </SidebarProvider>
            </SubscriptionProvider>
          </UserProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
